// PostCardCategory.jsx
import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import PostCard from '../container/PostCard.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';

import MoreIconSource from '../../img/ic-arrow-right-xs.svg';

const PostCardPlaceholder = () => {
  const result = useMemo(() => {
    return (
      <GridList>
        {new Array(9).fill(null).map((id, index) => (
          <PostCardWrapper key={index}>
            <PostCardLink>
              <PostCard />
            </PostCardLink>
          </PostCardWrapper>
        ))}
      </GridList>
    );
  }, []);
  return result;
};

export const PostCardCategory = ({
  title = '',
  category = '',
  moreLink = '',
  titleLink = '',
  nextPage = null,
  totalCount = 0,
  feedUnixTimestamp = null,
  index = null,
  isNextPageFetching = false,
  isFirstPageFetched = false,
  shouldRefresh = false,
  shouldShowTotalCount = false,
  shouldShowMoreLink = false,
  isProfileCategory = false,
  isTitleClickable = false,
  messageIds = [],
  fetchFeeds = () => null,
}) => {
  const { t } = useTranslation();
  const feedUnixTimestampRef = useRef(feedUnixTimestamp);
  const fetchData = useCallback(
    ({ page = 1, shouldSkipLocalCache }) => {
      if (category) {
        fetchFeeds({
          type: category,
          page,
          shouldSkipLocalCache,
          unixTimestamp: feedUnixTimestamp,
        });
      }
    },
    [category, feedUnixTimestamp, fetchFeeds]
  );
  useEffect(() => {
    if (shouldRefresh) {
      fetchData({ page: 1, shouldSkipLocalCache: true });
    }
  }, [fetchData, shouldRefresh]);
  useEffect(() => {
    const isChanged = feedUnixTimestampRef.current !== feedUnixTimestamp;
    fetchData({ page: 1, shouldSkipLocalCache: isChanged });
    feedUnixTimestampRef.current = feedUnixTimestamp;
  }, [feedUnixTimestamp, fetchData]);
  if (isFirstPageFetched && !messageIds.length) {
    return null;
  }
  return (
    <StyledPostCardCategory>
      <Header>
        <Title
          to={titleLink}
          as={isTitleClickable ? Link : undefined}
          replace={isProfileCategory}
        >
          <TitleText>{title}</TitleText>
          {shouldShowTotalCount && <TotalCount>({totalCount || 0})</TotalCount>}
        </Title>
        {shouldShowMoreLink && (
          <MoreLink
            to={moreLink}
            data-element_id={ButtonId.All.ButtonMore}
            data-tracking_payload={{
              'discover.index': index,
              'discover.category': category,
            }}
          >
            {t('more', {
              ns: TranslationNamespace.GENERAL,
            })}
            <img src={MoreIconSource} aria-hidden />
          </MoreLink>
        )}
      </Header>
      <Body>
        {!isFirstPageFetched ? (
          <PostCardPlaceholder />
        ) : (
          <InfiniteScroller
            axis={'y'}
            useWindow={true}
            pageStart={1}
            hasMore={nextPage != null}
            loadMore={() => {
              fetchData({ page: nextPage });
            }}
            isFetching={isNextPageFetching}
            threshold={200}
          >
            <GridList>
              <ScrollItem loader={<PostCardWrapper />}>
                {messageIds
                  .filter(id => id)
                  .map((messageId, index) => {
                    return (
                      <PostCardWrapper key={messageId}>
                        <PostCardLink
                          as={Link}
                          to={`/post/${messageId}`}
                          aria-label={`navigate to /post/${messageId}`}
                          data-element_id={ButtonId.All.ButtonPostCard}
                          data-tracking_payload={{
                            messageId,
                            'discover.index': index,
                            'discover.category': category,
                          }}
                        >
                          <PostCard
                            postId={messageId}
                            categoryId={category}
                            id={messageId}
                            itemIndexInCategory={index}
                          />
                        </PostCardLink>
                      </PostCardWrapper>
                    );
                  })}
              </ScrollItem>
            </GridList>
          </InfiniteScroller>
        )}
      </Body>
    </StyledPostCardCategory>
  );
};

PostCardCategory.propTypes = {
  title: PropTypes.string,
  category: PropTypes.string,
  moreLink: PropTypes.string,
  titleLink: PropTypes.string,
  index: PropTypes.number,
  nextPage: PropTypes.number,
  totalCount: PropTypes.number,
  feedUnixTimestamp: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  shouldShowTotalCount: PropTypes.bool,
  shouldShowMoreLink: PropTypes.bool,
  isProfileCategory: PropTypes.bool,
  isTitleClickable: PropTypes.bool,
  messageIds: PropTypes.array,
  fetchFeeds: PropTypes.func,
};

const StyledPostCardCategory = styled.div``;

const Header = styled.header`
  margin-bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  overflow: hidden;
  margin-right: 10px;
  flex: auto;
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
    font-size: 18px;
  `}
`;

const TitleText = styled.div`
  ${TextEllipsis};
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
        font-size: 18px;
  `}
`;

const TotalCount = styled.span`
  padding-left: 8px;
  color: var(--font-color-primary-on-dark, #fff);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.014px;
  ${media.mobile`
    font-size: 12px;
    letter-spacing: 0.012px;
  `}
`;

const MoreLink = styled(Link)`
  flex: none;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  ${media.mobile`
    font-size: 12px;
  `}
  >img {
    margin-left: 4px;
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const Body = styled.main``;

const GridList = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 4px;
  ${media.mobile`
    padding-left: 2px;
    padding-right: 2px;
    grid-gap: 2px;
  `};
`;

const PostCardWrapper = styled.div`
  position: relative;
  padding-top: 100%;
`;

const PostCardLink = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
`;

export default PostCardCategory;
